import OpenAI from 'openai';
import { getApiConfig } from './api-config';
import { GroqAPI } from './groq';
import { logApiRequest } from './events';
import { SIMILARITY_PROMPT } from './prompts';

export async function findSemanticSimilarity(sourceQuestion: string, questionsToCompare: string[]): Promise<number[]> {
  const config = getApiConfig();

  if (config.provider === 'groq') {
    return GroqAPI.compareSimilarities(sourceQuestion, questionsToCompare);
  }

  if (!config.apiKey) {
    throw new Error('API key is missing');
  }

  try {
    const openai = new OpenAI({
      apiKey: config.apiKey,
      dangerouslyAllowBrowser: true
    });

    // Log request with metrics before making the API call
    logApiRequest({
      provider: 'openai',
      request: {
        sourceQuestion,
        questionsToCompare
      },
      metrics: {
        totalQuestionsCompared: questionsToCompare.length
      }
    });

    // Send all questions in a single request since GPT-4o-mini has a large context window
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: SIMILARITY_PROMPT.system
        },
        {
          role: "user",
          content: SIMILARITY_PROMPT.generateUserPrompt(sourceQuestion, questionsToCompare)
        }
      ],
      response_format: {
        type: "text"
      },
      temperature: 1,
      max_completion_tokens: 16383,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0
    });

    const content = response.choices[0].message.content;

    if (!content) {
      console.error('Empty response from OpenAI');
      return new Array(questionsToCompare.length).fill(0);
    }

    try {
      const scores = SIMILARITY_PROMPT.parseScores(content, questionsToCompare.length);
      const successfulMatches = scores.filter(score => score > 0.7).length;

      // Log response with updated metrics
      logApiRequest({
        provider: 'openai',
        request: {
          sourceQuestion,
          questionsToCompare
        },
        response: {
          content,
          scores,
          usage: response.usage
        },
        metrics: {
          totalQuestionsCompared: questionsToCompare.length,
          successfulComparisons: successfulMatches
        }
      });

      return scores;
    } catch (parseError) {
      console.error('Failed to parse scores:', parseError);
      console.error('Response content:', content);
      return new Array(questionsToCompare.length).fill(0);
    }
  } catch (error) {
    // Log error with metrics
    logApiRequest({
      provider: 'openai',
      request: {
        sourceQuestion,
        questionsToCompare
      },
      error: error instanceof Error ? error.message : 'Unknown error',
      metrics: {
        totalQuestionsCompared: questionsToCompare.length,
        error: true
      }
    });
    throw new Error(`Failed to compare questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}